import { broker, locateProxy } from '#~/agent/index.js';
import { currentLimits, limitCanUseCloudMode } from '#~/api/billing/limits.js';
import { StudioError } from '#~/error/index.js';
import { logger } from '#~/logger/index.js';
import type { Agent } from '@studio/models/agent';
import type { Credentials as M_Credentials } from '@studio/models/auth';
import type { Device } from '@studio/models/device';
import { freeze } from 'immer';
import type { ReactNode } from 'react';
import { createContext, useContext, useEffect } from 'react';
import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { setDeviceStatus, updateProfile } from '../../commands/index.js';
import { CLOUD_USAGE_LIMIT_ERROR_MESSAGE, isBillingLimitError } from '../billing-limit.js';
import { useGlobalStudioRTMPDestinations } from '../hooks/rtmp-destinations.js';
import type { ProfileConfig } from '../utils.js';
import { audioCodecsToEgressCodec, getProfileConfig, videoCodecsToEgressCodec } from '../utils.js';
import {
  markPendingAdd,
  markPendingDelete,
  reconcileDestinations
} from './destinations-reconcile.js';

const getCloudUsageLimitError = (deviceId: Device['id']): string | null => {
  // Action-time re-read: lock and plan limits can change after the card last rendered.
  const billingLocked = !!locateProxy(deviceId)?.state.control?.encoder?.billingLocked;
  const billingLimitsLoaded = currentLimits !== null;
  const cloudModeBlockedByLimits = billingLimitsLoaded && !limitCanUseCloudMode();

  if (billingLocked || cloudModeBlockedByLimits) {
    return CLOUD_USAGE_LIMIT_ERROR_MESSAGE;
  }

  return null;
};

export enum OperationType {
  Add = 'add',
  Remove = 'remove',
  Update = 'update'
}

export enum StreamOperation {
  Start = 'start',
  Stop = 'stop'
}

export type RTMPDestinationsMutationRecords = Record<
  string,
  | {
      destinationOptions?:
        | {
            url?: string;
            streamKey?: string;
            nickName?: string;
          }
        | undefined;
      deviceId?: string | undefined;
    }
  | undefined
>;

export interface Destination {
  id: string;
  nickName: string;
  url: string;
  streamKey: string;
  requireAuth: boolean;
  credentials: M_Credentials;
  selectedSourceId?: Device['id'] | undefined;
}

const getAgentIdFromDeviceId = (deviceId: Device['id']): Agent['id'] | undefined => {
  const proxy = locateProxy(deviceId);
  return proxy?.id;
};

const syncDestinationsToStudio = async (
  destinations: Destination[],
  token: string,
  operation: OperationType,
  agentId: Agent['id'] | undefined,
  targetDestination?: Destination
): Promise<void> => {
  try {
    let proxy = null;

    if (agentId) {
      proxy = broker.acquire(agentId);
    }

    // If no proxy found from destinations, we can't sync yet
    if (!proxy) {
      return;
    }

    let studioDestinations: RTMPDestinationsMutationRecords = {};

    if (operation === OperationType.Add && targetDestination) {
      const uuid = targetDestination.id.split('_')[0] as string;
      const deviceId = targetDestination.id.split('_')[2] as Device['id'];

      studioDestinations = {
        [uuid]: {
          deviceId: deviceId ?? undefined,
          destinationOptions: {
            url: targetDestination?.url ?? '',
            streamKey: targetDestination?.streamKey ?? '',
            nickName: targetDestination?.nickName ?? ''
          }
        }
      };
    }

    if (operation === OperationType.Remove && targetDestination) {
      // For remove operations, create mutation object with the destination to remove
      const uuid = targetDestination.id.split('_')[0] as string;
      studioDestinations = {
        [uuid]: {}
      };
    }

    if (operation === OperationType.Update) {
      for (const dest of destinations) {
        const uuid = dest.id.split('_')[0] as string;
        const deviceId = dest.id.split('_')[2] as Device['id'];

        studioDestinations[uuid] = {
          deviceId: deviceId ?? undefined,
          destinationOptions: {
            url: dest?.url ?? '',
            streamKey: dest?.streamKey ?? '',
            nickName: dest?.nickName ?? ''
          }
        };
      }
    }

    const mutation = {
      control: {
        studio: {
          rtmpEgress: {
            destination: studioDestinations
          }
        }
      }
    };

    if ([OperationType.Add, OperationType.Update].includes(operation)) {
      await proxy.mutate(mutation, {});
    }
    if ([OperationType.Remove].includes(operation)) {
      await proxy.mutate({}, mutation);
    }

    // This is the call that *actually* saves the state.control object to the backend
    await proxy.save(token);
  } catch (cause) {
    console.error('___syncDestinationsToStudio error:', cause);
    throw new StudioError('Unable to sync destinations to studio object', { cause });
  }
};

interface State {
  destinations: Destination[];
  isInitialized: boolean;
  // Echo-wait flags: keep a just-saved dest through an empty snapshot, and
  // block resurrection from a stale snapshot after a local delete.
  pendingAddIds: string[];
  pendingDeleteIds: string[];
  // ####### destination management
  addDestination: (
    destination: Destination,
    agentId: Agent['id'] | undefined,
    deviceId?: Device['id'],
    token?: string
  ) => Promise<void>;
  removeDestination: (
    destination: Destination,
    agentId: Agent['id'] | undefined,
    deviceId?: Device['id'],
    token?: string
  ) => Promise<void>;
  updateDestination: (
    destinationId: string,
    agentId: Agent['id'] | undefined,
    deviceId?: Device['id'],
    token?: string,
    updates?: {
      url: string;
      streamKey: string;
      nickName: string;
    }
  ) => Promise<void>;
  // ####### source selection
  setSelectedSource: (destinationId: string, sourceId: Device['id']) => void;
  // ####### streaming
  startStreaming: (
    deviceId: Device['id'],
    destination: Destination,
    profileConfig?: ProfileConfig
  ) => Promise<void>;
  stopStreaming: (
    deviceId: Device['id'],
    destination: Destination,
    profileConfig?: ProfileConfig
  ) => Promise<void>;
  // ####### profile management
  updateProfile: (config: ProfileConfig) => Promise<void>;
  // ####### state synchronization
  syncFromGlobalState: (globalDestinations: Destination[]) => void;
  // ####### state properties (now sourced from global studio state)
  // ####### immer
  produce: (fn: (draft: State) => void) => void;
}

const initialState: Pick<
  State,
  'destinations' | 'isInitialized' | 'pendingAddIds' | 'pendingDeleteIds'
> = {
  destinations: [],
  isInitialized: false,
  pendingAddIds: [],
  pendingDeleteIds: []
};

// Store
const store = create<State>()(
  immer((set, get) =>
    freeze({
      ...initialState,
      addDestination: async (destination, agentId, deviceId, token) => {
        const state = get();
        if (state.destinations.length >= 25) {
          throw new Error('Maximum of 25 destinations allowed');
        }

        // Validate destination data
        if (!destination.nickName?.trim()) {
          throw new Error('Destination nickname is required');
        }
        if (
          !destination.url?.trim() ||
          (!destination.url.startsWith('rtmp://') && !destination.url.startsWith('rtmps://'))
        ) {
          throw new Error('Valid RTMP server URL is required');
        }
        if (!destination.streamKey?.trim()) {
          throw new Error('Stream key is required');
        }

        set((state) => {
          const existingIndex = state.destinations.findIndex((d) => d.id === destination.id);
          if (existingIndex >= 0) {
            state.destinations[existingIndex] = destination;
          } else {
            state.destinations.push(destination);
          }
          // Same mutation: dest is visible immediately and echo-wait starts.
          const pending = markPendingAdd(
            {
              pendingAddIds: state.pendingAddIds,
              pendingDeleteIds: state.pendingDeleteIds
            },
            destination.id
          );
          state.pendingAddIds = [...pending.pendingAddIds];
          state.pendingDeleteIds = [...pending.pendingDeleteIds];
        });

        // Sync destinations to studio object for persistence across bridge restarts
        try {
          const finalAgentId = agentId ?? (deviceId ? getAgentIdFromDeviceId(deviceId) : undefined);
          await syncDestinationsToStudio(
            get().destinations,
            token!,
            OperationType.Add,
            finalAgentId,
            destination
          );
        } catch (error) {
          console.error('___addDestination studio sync failed:', error);
        }
      },
      removeDestination: async (destination, agentId, deviceId, token) => {
        // First remove the destination from local state
        set((state) => {
          state.destinations = state.destinations.filter((d) => d.id !== destination.id);
          // Same mutation: dest leaves the grid and a stale echo cannot resurrect it.
          const pending = markPendingDelete(
            {
              pendingAddIds: state.pendingAddIds,
              pendingDeleteIds: state.pendingDeleteIds
            },
            destination.id
          );
          state.pendingAddIds = [...pending.pendingAddIds];
          state.pendingDeleteIds = [...pending.pendingDeleteIds];
        });

        try {
          const finalAgentId = agentId ?? (deviceId ? getAgentIdFromDeviceId(deviceId) : undefined);
          await syncDestinationsToStudio(
            get().destinations,
            token!,
            OperationType.Remove,
            finalAgentId,
            destination
          );
        } catch (error) {
          console.error('___removeDestination studio sync failed:', error);
        }
      },
      updateDestination: async (destinationId, agentId, deviceId, token, updates) => {
        if (!updates) return;

        set((state) => {
          const destination = state.destinations.find((d) => d.id === destinationId);
          if (destination && updates) {
            if (updates.url) destination.url = updates.url;
            if (updates.streamKey) destination.streamKey = updates.streamKey;
            if (updates.nickName) destination.nickName = updates.nickName;
          }
        });

        // Sync destinations to studio object for persistence across bridge restarts
        try {
          const finalAgentId = agentId ?? (deviceId ? getAgentIdFromDeviceId(deviceId) : undefined);
          await syncDestinationsToStudio(
            get().destinations,
            token!,
            OperationType.Update,
            finalAgentId
          );
        } catch (error) {
          console.error('___updateDestination studio sync failed:', error);
          // Don't throw here to avoid breaking the UI flow
        }
      },
      setSelectedSource: (destinationId, sourceId) => {
        set((state) => {
          const destination = state.destinations.find((d) => d.id === destinationId);
          if (destination) {
            destination.selectedSourceId = sourceId;
          }
        });
      },
      startStreaming: async (deviceId, destination, profileConfig) => {
        const billingLimitError = getCloudUsageLimitError(deviceId);
        if (billingLimitError) {
          throw new Error(billingLimitError);
        }

        try {
          // Use provided profile config or fall back to default (1080P-30FPS)
          const finalProfileConfig = profileConfig || getProfileConfig('1080P-30FPS', deviceId);

          await setDeviceStatus(deviceId, false, {
            rtmp: {
              enable: true,
              destination,
              profileConfig: finalProfileConfig,
              operation: StreamOperation.Start
            }
          });
        } catch (error) {
          logger.error('Failed to start RTMP streaming', error);
          if (isBillingLimitError(error)) {
            throw new Error(CLOUD_USAGE_LIMIT_ERROR_MESSAGE);
          }

          throw new Error(
            `Failed to start streaming: ${error instanceof Error ? error.message : 'Unknown error'}`
          );
        }
      },
      stopStreaming: async (deviceId, destination, profileConfig) => {
        if (!destination.id?.trim()) {
          throw new Error('Destination ID is required');
        }
        if (!deviceId?.trim()) {
          throw new Error('Device ID is required');
        }

        // Use provided profile config or fall back to default (1080P-30FPS)
        const finalProfileConfig = profileConfig || getProfileConfig('1080P-30FPS', deviceId);

        try {
          await setDeviceStatus(deviceId, false, {
            rtmp: {
              enable: false,
              destination,
              profileConfig: finalProfileConfig,
              operation: StreamOperation.Stop
            }
          });
        } catch (error) {
          console.error('Failed to stop streaming:', error);
          throw new Error(
            `Failed to stop streaming: ${error instanceof Error ? error.message : 'Unknown error'}`
          );
        }
      },
      updateProfile: async (config) => {
        if (!config.deviceId?.trim()) {
          throw new Error('Device ID is required');
        }

        try {
          // Convert the ProfileConfig to the format expected by updateProfile command
          const commandConfig = {
            deviceId: config.deviceId,
            width: config.width,
            height: config.height,
            depth: config.depth,
            frameRate: config.frameRate,
            audioCodec: audioCodecsToEgressCodec[config.audioCodec],
            audioBitrate: config.audioBitrate,
            audioFrequency: config.audioFrequency,
            videoCodec: videoCodecsToEgressCodec[config.videoCodec],
            videoBitrate: config.videoBitrate,
            keyFrameInterval: config.keyFrameInterval
          };

          await updateProfile(commandConfig);
        } catch (error) {
          console.error('Failed to update profile:', error);
          throw new Error(
            `Failed to update profile: ${error instanceof Error ? error.message : 'Unknown error'}`
          );
        }
      },
      syncFromGlobalState: (globalDestinations) => {
        set((state) => {
          // Per rule: ci-only-failures-fix-the-product-first (ai.knowledge/global)
          // — studio echo that omits a just-saved dest must not wipe the card.
          // Always reconcile so a matching-id echo can clear pending-add;
          // leftover pending-add would hide a later remote delete.
          const next = reconcileDestinations(state.destinations, globalDestinations, {
            pendingAddIds: state.pendingAddIds,
            pendingDeleteIds: state.pendingDeleteIds
          });
          // Skip the dest write when merge equals local — a new array remounts
          // the card and detaches dest-scoped Start/source locators.
          const sameAsLocal =
            next.destinations.length === state.destinations.length &&
            next.destinations.every((dest, index) => {
              const current = state.destinations[index];
              return (
                current !== undefined &&
                dest.id === current.id &&
                dest.selectedSourceId === current.selectedSourceId
              );
            });
          const samePending =
            next.pendingAddIds.length === state.pendingAddIds.length &&
            next.pendingAddIds.every((id, index) => id === state.pendingAddIds[index]) &&
            next.pendingDeleteIds.length === state.pendingDeleteIds.length &&
            next.pendingDeleteIds.every((id, index) => id === state.pendingDeleteIds[index]);
          // Capture before mutating isInitialized — otherwise wrote is wrong
          // after the first successful init write.
          const wrote = !state.isInitialized || !sameAsLocal;
          if (wrote) {
            state.destinations = next.destinations;
            state.isInitialized = true;
          }
          if (!samePending) {
            state.pendingAddIds = next.pendingAddIds;
            state.pendingDeleteIds = next.pendingDeleteIds;
          }
        });
      },
      produce: (fn) => set(fn)
    })
  )
);

// Context
const DestinationsContext = createContext<typeof store | null>(null);

// Provider
export const DestinationsProvider = ({ children }: { children: ReactNode }) => (
  <DestinationsContext.Provider value={store}>{children}</DestinationsContext.Provider>
);

// Hooks
export function useDestinationsStore<T>(selector: (state: State) => T): T {
  const s = useContext(DestinationsContext);
  if (!s) throw new Error('useDestinationsStore must be used within DestinationsProvider');
  return s(selector);
}

export function useAddDestination() {
  return useDestinationsStore((s) => s.addDestination);
}

export function useRemoveDestination() {
  return useDestinationsStore((s) => s.removeDestination);
}

export function useUpdateDestination() {
  return useDestinationsStore((s) => s.updateDestination);
}

export function useStartStreaming() {
  return useDestinationsStore((s) => s.startStreaming);
}

export function useStopStreaming() {
  return useDestinationsStore((s) => s.stopStreaming);
}

export function useUpdateProfile() {
  return useDestinationsStore((s) => s.updateProfile);
}

export function useDestinationsInitialized() {
  return useDestinationsStore((s) => s.isInitialized);
}

export function useSetSelectedSource() {
  return useDestinationsStore((s) => s.setSelectedSource);
}

export function useSelectedSource(destinationId: string): Device['id'] | undefined {
  return useDestinationsStore((s) => {
    const destination = s.destinations.find((d) => d.id === destinationId);
    return destination?.selectedSourceId;
  });
}

export function useSyncFromGlobalState() {
  return useDestinationsStore((s) => s.syncFromGlobalState);
}

export function useDestinations(): Destination[] {
  return useDestinationsStore((s) => s.destinations);
}

/**
 * Hook that automatically syncs destinations from global studio state to the local store.
 * This should be used in components that need to ensure the store is up-to-date with global state.
 */
export function useDestinationsWithGlobalSync(): Destination[] {
  const globalDestinations = useGlobalStudioRTMPDestinations();
  const syncFromGlobalState = useSyncFromGlobalState();
  const destinations = useDestinations();

  useEffect(() => {
    syncFromGlobalState(globalDestinations);
  }, [globalDestinations, syncFromGlobalState]);

  return destinations;
}
